import React from 'react';
import { Alert, Space, Tag, Tooltip } from 'antd';

import { Recommendation } from './matching.types';
import { PageWideState, processingReason, RowFlags } from './matchingPresentation';

export const RowStatusTags = ({ flags }: { flags: RowFlags }) => (
  <Space size={[0, 4]} wrap>
    {flags.outdated && <Tag color="gold">Outdated assessment</Tag>}
    {flags.versionUnverified && <Tag color="gold">Version unverified</Tag>}
    {flags.notAutoUpdatedReason && (
      <Tooltip title={processingReason(flags.notAutoUpdatedReason)}>
        <Tag>Not auto-updated</Tag>
      </Tooltip>
    )}
    {flags.updating && <Tag color="blue">{flags.updating}</Tag>}
    {flags.failed && <Tag color="red">Update failed</Tag>}
  </Space>
);

// States shared by every row on the page are stated once here instead of on each row.
export const PageWideStateBanner = ({ rows, pageWide }: { rows: Recommendation[]; pageWide: Set<PageWideState> }) => {
  const reasons = Array.from(new Set(rows.map((row) => row.processing.blockedReason)));
  const messages = [
    pageWide.has('stale') &&
      'These matches may be out of date because candidate data, project requirements or the assessment version changed.',
    pageWide.has('notAutoUpdated') &&
      (reasons.length === 1 && reasons[0]
        ? `These matches are not updated automatically: ${processingReason(reasons[0])}.`
        : 'These matches are not updated automatically.'),
    pageWide.has('budgetUnknown') &&
      'Budgets cannot be compared for these matches because a candidate salary expectation or the project maximum is missing.',
  ].filter((message): message is string => !!message);
  if (!messages.length) return null;
  return (
    <Alert
      className="matching-uniform-banner"
      type="info"
      showIcon
      message={
        messages.length === 1 ? (
          messages[0]
        ) : (
          <ul className="matching-reasons">
            {messages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        )
      }
    />
  );
};

export const MatchingProcessingNotice = ({
  processingEnabled,
  processingActive,
  processingBlockedReason,
}: {
  processingEnabled: boolean;
  processingActive: boolean;
  processingBlockedReason: string | null;
}) => {
  if (!processingEnabled || processingBlockedReason)
    return (
      <Alert
        type="warning"
        showIcon
        message="Matching updates are paused"
        description={`${processingReason(
          processingBlockedReason || 'processing-disabled'
        )}. Stored assessments remain available.`}
      />
    );
  if (processingActive)
    return (
      <Alert
        type="info"
        showIcon
        message="Matching is in progress"
        description="Results update automatically while processing continues. You can inspect available assessments now."
      />
    );
  return null;
};
